import React, { useEffect, useMemo, useState } from 'react'
import { subscribe } from 'react-contextual'
import { get } from 'lodash-es'
import { AccountSelector, Button } from '../../../../../views/components'
import AccountDetails from './AccountDetails/AccountDetails'
import { ContactsProvider } from '../../..'
import styles from './AccountPanel.module.scss'
import ActiveInvoicesProvider from './ActiveInvoices/ActiveInvoicesProvider'
import ActiveInvoices from './ActiveInvoices/ActiveInvoices'
import ContactBalanceProvider from './ContactBalance/ContactBalanceProvider'
import ContactBalance from './ContactBalance/ContactBalance'
import ConsolidatedBalanceProvider from './ConsolidatedBalance/ConsolidatedBalanceProvider'
import ConsolidatedBalance from './ConsolidatedBalance/ConsolidatedBalance'
import RecentActivityProvider from './RecentActivity/RecentActivityProvider'
import RecentActivity from './RecentActivity/RecentActivity'
import { RefundModal } from '../../../../depositManagement/components'
import RefundProvider from '../../../../depositManagement/RefundProvider'
import { $TSFixMe } from 'types/ts-migrate'
import { TAccount } from 'views/components/molecules/AccountSelector/AccountSelector'
import { useSelector } from 'react-redux'
import { portfolioApiSelectors } from 'modules/api/portfolio'
import { walletApiSelectors } from 'modules/api/wallet'
import { groupBy } from 'ramda'
import DepositAccount from './DepositAccount/DepositAccount'
import DepositAccountProvider from './DepositAccount/DepositAccountProvider'

const mapAccountFields = (account: $TSFixMe): TAccount => ({
  id: account.accountId,
  value: account.accountId,
  label: account.propertyAddress ? account.propertyAddress : 'No attached lease',
  tag: account.tag,
  portfolioId: account?.portfolioId,
  paymentReference: account.paymentReference,
})

const createConsolidatedBalanceAccount = (partyId: string, paymentReference: string): TAccount => ({
  id: partyId,
  value: partyId,
  label: 'No attached lease',
  tag: 'Owner',
  paymentReference,
  isConsolidatedBalance: true,
})

// The account the consolidated balance represents has no portfolioId. Depending on which endpoint
// hydrated the store it comes back tagged either 'Owner' (quick search) or 'Consolidated' (party
// entity), so match both. It's merged into — and replaced by — the synthetic consolidated entry, so
// it gets dropped from the account list.
const isUnattachedConsolidatedAccount = (account: TAccount) =>
  !account.portfolioId && (account.tag === 'Owner' || account.tag === 'Consolidated')

type TAccountWithStatus = TAccount & { status: string }

const AccountPanel = ({
  id,
  currentParty,
  setCurrentAccountId,
  partyHasBankDetails,
  partyAccountBalance,
  partyAccountAsset,
  openContactStatement,
  openContactConsolidatedStatement,
}: any) => {
  const [selectedAccount, setSelectedAccount] = useState<$TSFixMe>(null)
  const accounts = get(currentParty, 'accounts', []).map(mapAccountFields)

  const statusOrder = ['active', 'expiring', 'renewal', 'draft', 'expired', 'terminated', 'deleted']
  const getPortfolioStatusLabel = useSelector(portfolioApiSelectors.getPortfolioStatusLabel)
  const accountsWithPortfolioStatus = accounts.map(account => ({
    ...account,
    status: getPortfolioStatusLabel(account.portfolioId, true),
  }))

  const groupedAccounts = groupBy((account: TAccountWithStatus) => account.status, accountsWithPortfolioStatus)

  const sortedAccounts = statusOrder
    .map(status => groupedAccounts[status || ''])
    .filter(Boolean)
    .flat()

  const moveApplicationAccountsToBottom = (accounts: TAccountWithStatus[]) => {
    const applicationAccounts = accounts.filter(account => account.tag === 'Application')
    const nonApplicationAccounts = accounts.filter(account => account.tag !== 'Application')
    return [...nonApplicationAccounts, ...applicationAccounts]
  }

  const movedApplicationAccounts = moveApplicationAccountsToBottom(sortedAccounts)

  const getConsolidatedBalanceAsset = useSelector(walletApiSelectors.getConsolidatedBalanceAsset)
  const getConsolidatedBalance = useSelector(walletApiSelectors.getConsolidatedBalance)
  const hasConsolidatedBalance = !!getConsolidatedBalanceAsset(currentParty?.id)

  // The consolidated entry's reference comes straight from the wallet's consolidatedBalance
  // (accountReference) — the authoritative source — not the party accounts, whose shape varies by
  // endpoint. The unattached account it represents is then dropped from the list below.
  const getConsolidatedBalanceReference = useSelector(walletApiSelectors.getConsolidatedBalanceReference)
  const consolidatedAccount = hasConsolidatedBalance
    ? createConsolidatedBalanceAccount(currentParty?.id, getConsolidatedBalanceReference(currentParty?.id))
    : null
  const optionsWithConsolidated = consolidatedAccount
    ? [consolidatedAccount, ...movedApplicationAccounts.filter(account => !isUnattachedConsolidatedAccount(account))]
    : movedApplicationAccounts

  useEffect(() => {
    // Prefer the synthetic consolidated balance account when one exists — it's the top option.
    if (consolidatedAccount) {
      setSelectedAccount(consolidatedAccount)
      return
    }
    // Otherwise select the first real account.
    const selectedAccount = get(currentParty, 'accounts', []).find(
      (acc: $TSFixMe) => acc.accountId === movedApplicationAccounts[0]?.id,
    )
    if (selectedAccount) {
      setSelectedAccount(mapAccountFields(selectedAccount))
    }
    // Depend on the resolved top-account id too: when arriving with a party that was
    // already in the store (e.g. from a lease) the accounts load in later via
    // party_success without changing currentParty.id, so keying on id alone would never
    // select a default account and the panel (and its balance_request) would never mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentParty?.id, movedApplicationAccounts[0]?.id, hasConsolidatedBalance])

  useEffect(() => {
    if (selectedAccount?.id) {
      setCurrentAccountId(selectedAccount.id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccount])

  const account = useMemo(() => {
    // Check if it's the synthetic consolidated balance account
    if (selectedAccount?.isConsolidatedBalance) {
      return selectedAccount
    }
    // Otherwise find in real accounts
    return accounts?.find(acc => acc.id === get(selectedAccount, 'id'))
  }, [accounts, selectedAccount])

  const isConsolidatedBalance = account?.isConsolidatedBalance

  // The consolidated balance lives in a separate wallet slice (consolidatedBalance) and is not keyed
  // by accountId, so the account-keyed partyAccountBalance/partyAccountAsset resolve to 0/false for
  // it. Feed AccountDetails the consolidated figures instead so the withdraw flow works — mirroring
  // the balance-provider branch below.
  const withdrawBalance = isConsolidatedBalance ? getConsolidatedBalance(currentParty?.id) : partyAccountBalance
  const withdrawAsset = isConsolidatedBalance ? getConsolidatedBalanceAsset(currentParty?.id) : partyAccountAsset

  if (!currentParty) {
    return null
  }

  return !account ? null : (
    <div className={styles.root}>
      <section className="account-selector">
        <AccountSelector
          name="active-account"
          options={optionsWithConsolidated}
          onChange={(data: any) => setSelectedAccount(data)}
          value={account}
        />
      </section>
      <section>
        <RefundProvider entity="party" id={id} tenantAccountId={selectedAccount?.id}>
          <AccountDetails
            paymentReference={get(account, 'paymentReference')}
            accountId={isConsolidatedBalance ? undefined : get(account, 'id')}
            isConsolidatedBalance={isConsolidatedBalance}
            accountTag={get(account, 'tag')}
            accountAddress={get(account, 'label')}
            status="good"
            partyId={currentParty.id}
            partyHasBankDetails={partyHasBankDetails}
            partyAccountBalance={withdrawBalance}
            partyAccountAsset={withdrawAsset}
            portfolioId={selectedAccount?.portfolioId}
          />
          <RefundModal />
        </RefundProvider>
      </section>
      {!isConsolidatedBalance ? (
        <ContactBalanceProvider customerId={currentParty.id} accountId={account.id} accountType={account.tag}>
          <ContactBalance />
        </ContactBalanceProvider>
      ) : (
        <ConsolidatedBalanceProvider customerId={currentParty.id}>
          <ConsolidatedBalance />
        </ConsolidatedBalanceProvider>
      )}

      <ActiveInvoicesProvider
        customerId={currentParty.id}
        accountId={account.id}
        isConsolidatedBalance={isConsolidatedBalance}
      >
        <ActiveInvoices />
      </ActiveInvoicesProvider>
      <section>
        <header>
          <span>
            {isConsolidatedBalance
              ? 'Consolidated Statement'
              : get(account, 'tag') === 'Tenant'
              ? 'Tenant Statement'
              : 'Account Statement'}
          </span>
          <Button
            pill
            tertiary
            size="sm"
            onClick={() =>
              isConsolidatedBalance ? openContactConsolidatedStatement() : openContactStatement(account.id)
            }
          >
            View statement
          </Button>
        </header>
      </section>
      {!isConsolidatedBalance && (
        <>
          <DepositAccountProvider partyId={currentParty.id} accountId={account.id} accountType={account.tag}>
            <RefundProvider entity="party" id={id} tenantAccountId={selectedAccount?.id}>
              <DepositAccount
                isTenant={currentParty.tags.includes('Tenant')}
                partyId={id}
                portfolioId={selectedAccount?.portfolioId}
                accountId={selectedAccount?.id}
                accountType={selectedAccount.tag}
              />
              <RefundModal />
            </RefundProvider>
          </DepositAccountProvider>

          <RecentActivityProvider customerId={currentParty.id} accountId={account.id}>
            <RecentActivity />
          </RecentActivityProvider>
        </>
      )}
    </div>
  )
}

export default subscribe(
  ContactsProvider,
  ({
    id,
    currentParty,
    setCurrentAccountId,
    partyHasBankDetails,
    partyAccountBalance,
    partyAccountAsset,
    openContactStatement,
    openContactConsolidatedStatement,
  }: any) => ({
    id,
    currentParty,
    setCurrentAccountId,
    partyHasBankDetails,
    partyAccountBalance,
    partyAccountAsset,
    openContactStatement,
    openContactConsolidatedStatement,
  }),
)(AccountPanel)
